/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { Note } from "./note";

export const MAJOR_KEYS = [ 'C', 'F', 'B♭', 'E♭', 'A♭', 'C♯/D♭', 'F♯/G♭', 'B/C♭',  'E',  'A',  'D', 'G' ];
export const MINOR_KEYS = [ 'A', 'D', 'G',  'C',  'F',  'A♯/B♭', 'D♯/E♭', 'A♭/G♯', 'C♯', 'F♯', 'B', 'E' ];

const MAJOR_BASES = [ 0, 5, 10, 3, 8, 1, 6, 11, 4, 9, 2, 7 ];
const MINOR_BASES = [ 9, 2, 7, 0, 5, 10, 3, 8, 1, 6, 11, 4 ];


export const MODES = [ 'major', 'minor' ];

// accidentals adjusted by major key
const NAMES_BY_KEY = new Map<string, string[]>([
    [ 'C',     [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'F',     [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'B♭',    [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'E♭',    [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'A♭',    [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'C♯/D♭', [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'F♯/G♭', [ 'C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B' ] ],
    [ 'B/C♭',  [ 'C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B' ] ],
    [ 'E',     [ 'C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B' ] ],
    [ 'A',     [ 'C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B' ] ],
    [ 'D',     [ 'C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B' ] ],
    [ 'G',     [ 'C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B' ] ]
]);


const MAJOR_STEPS = [ 0, 2, 4, 5, 7, 9, 11, 12 ];
const MINOR_STEPS = [ 0, 2, 3, 5, 7, 8, 10, 12 ];

export class MusicKey {

    /** e.g. 'C', 'F', 'C♯/D♭' */
    readonly key : string = 'C';

    /** 'major' or 'minor' */
    readonly mode : "major" | "minor" = 'major';

    /** start step (0 - 11) of a scale in this key */
    readonly base : number = 0;

    /** combined name */
    get name() { return `${this.key} ${this.mode}`; }

    /** short name without the slash and alias key */
    get shortName() { return this.key.split('/')[0] + " " + this.mode; }

    get isMajor() { return this.mode === 'major'; }
    get isMinor() { return this.mode === 'minor'; }

    /** map name to vex compatible key signature */
    get signature() {
        const k = this.relativeMajor.key.split('/')[0].replace('♭', 'b').replace('♯', '#');
        return (k.length > 2) ? k.substring(0, 2) : k;
    }


    /** create a key from the given name (e.g. 'A minor') */
    constructor(name : string) {
        const s = name.split(' ');
        if (s.length === 2) {
            if (s[1].toLowerCase() === 'major' && MAJOR_KEYS.includes(s[0])) {
                this.key = s[0];
                this.mode = 'major';
                this.base = MAJOR_BASES[MAJOR_KEYS.indexOf(s[0])];
            }
            else if (s[1].toLowerCase() === 'minor' && MINOR_KEYS.includes(s[0])) {
                this.key = s[0];
                this.mode = 'minor';
                this.base = MINOR_BASES[MINOR_KEYS.indexOf(s[0])];
            }
        }
    }


    get relativeMajor() : MusicKey {
        return (this.mode === 'minor') ? new MusicKey(MAJOR_KEYS[MINOR_KEYS.indexOf(this.key)] + ' major') : this;
    }

    get relativeMinor() : MusicKey {
        return (this.mode === 'major') ? new MusicKey(MINOR_KEYS[MAJOR_KEYS.indexOf(this.key)] + ' minor') : this;
    }


    /** key-specific note name with correct accidental indicator */
    getNoteName(note : Note, includeAccidental : boolean = true) : string {
        const major = this.relativeMajor;
        const names = NAMES_BY_KEY.get(major.key);
        const name = names ? names[note.step] : note.name;
        return includeAccidental? name : this.stripAccidental(name);
    }


    /** key-specific python variable name with accidental and octave for a given note */
    getNoteVariableName(note : Note) : string {
        const major = this.relativeMajor;
        const names = NAMES_BY_KEY.get(major.key);
        const name = `${names ? names[note.step] : note.name}${note.octave - 1}`;
        return name.replace('♯', 's').replace('♭', 'b');
    }


    /** key-specific accidental for the given note */
    getAccidental(note : Note) : '♯' | '♭' | '♮' | '' {
        const name = this.getNoteName(note);

        // no accidental if the note is in the given key
        if (this.containsNote(note)) {
            return '';
        } 
        // natural notes not in key need a natural accidental
        else if (name.length === 1) {
            return '♮';
        }
        else {
            return name.endsWith('♯') ? '♯' : name.endsWith('♭') ? '♭' : '';
        }
    }

    /** Returns the proper diatonic chord roman numeral based on the key and a given chord number */
    getChordRoman(chord : number) : string {
        switch(chord) {
            case 1: return this.isMajor ? 'I' : 'i';
            case 2: return this.isMajor ? 'ii' : 'ii°';
            case 3: return this.isMajor ? 'iii' : 'III';
            case 4: return this.isMajor ? 'IV' : 'iv';
            case 5: return 'V';
            case 6: return this.isMajor ? 'vi' : 'VI';
            case 7: return 'vii°';
            default: return this.isMajor ? 'I' : 'i';
        }
    }

    /** Returns the proper diatonic chord name based on the key and a given chord number */
    getChordName(chord : number) : string {
        chord = Math.max(1, Math.min(7, chord));
        let r = this.base + 24;
        r += this.isMajor ? MAJOR_STEPS[chord - 1] : MINOR_STEPS[chord - 1];
        const root = this.getNoteName(new Note(r));
        switch(chord) {
            case 1: return root + (this.isMajor ? 'maj' : 'min');
            case 2: return root + (this.isMajor ? 'min' : 'dim');
            case 3: return root + (this.isMajor ? 'min' : 'maj');
            case 4: return root + (this.isMajor ? 'maj' : 'min');
            case 5: return root + 'maj';
            case 6: return root + (this.isMajor ? 'min' : 'maj');
            case 7: return root + 'dim';
            default: return root + (this.isMajor ? 'maj' : 'min');
        }
    }

    /** Returns a list of notes representing the given triad starting in the 4th octave */
    getDiatonicTriad(chord : number, octave : number = 4) : number[] {
        chord = Math.max(1, Math.min(7, chord));
        let r = this.base + 12 * octave;
        r += this.isMajor ? MAJOR_STEPS[chord - 1] : MINOR_STEPS[chord - 1];
        const maj = [ r, r + 4, r + 7 ];
        const min = [ r, r + 3, r + 7 ];
        const dim = [ r, r + 3, r + 6 ];
        switch(chord) {
            case 1: return this.isMajor ? maj : min;
            case 2: return this.isMajor ? min : dim;
            case 3: return this.isMajor ? min : maj;
            case 4: return this.isMajor ? maj : min;
            case 5: return maj;
            case 6: return this.isMajor ? min : maj;
            case 7: return dim;
            default: return this.isMajor ? maj : min;
        }
    }

    /** Does this key naturally include this note (should it be displayed without an accidental)? */
    containsNote(note : Note) : boolean {
        const step = note.step + 12; // [ 12 - 23 ]
        const base = this.base;      // [ 0 - 11 ]
        const delta = (step - base) % 12; // [ 0 - 11 ]
        return this.isMajor ? MAJOR_STEPS.includes(delta) : MINOR_STEPS.includes(delta);
    }

    private stripAccidental(name : string) : string {
        if (name.endsWith('♯') || name.endsWith('#') || 
            name.endsWith('♭') || name.endsWith('b') ||
            name.endsWith('♮') || name.endsWith('n')) {
            return name.substring(0, name.length - 1);
        } else {
            return name;
        }
    }
}
